import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { adminApi } from "@/lib/api";
import {
  Rocket, CircleCheck, CircleX, RefreshCw, Bot, Users, Server, Lock, Bug, History, Printer,
} from "lucide-react";

/** Phase 11.8 — PUBLIC LAUNCH READINESS (the Launch Gate).
 * Distinguishes AUTOMATED test evidence from HUMAN rehearsal evidence.
 * LIVE email and any full public-launch switch are impossible while a required gate is red
 * (enforced server-side — this page is the visible truth of that lock). */

const GROUP_META = {
  automated: {
    icon: Bot, title: "Automated tests",
    sub: "Suites recorded green. Necessary — but an automated green test is NOT a substitute for a real person completing the workflow unaided.",
  },
  human: {
    icon: Users, title: "Human rehearsal",
    sub: "Real people, uncoached. All four must be green for the Human Rehearsal gate to pass.",
  },
  system: {
    icon: Server, title: "System & operations",
    sub: "Live platform state, coverage truth and operational readiness.",
  },
};

const ConfirmGateDialog = ({ gate, onDone }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(gate.note || "");
  const [reason, setReason] = useState("");
  const set = async (ok) => {
    try {
      await adminApi.patch("/admin/launch-gate",
        { key: gate.key, ok, note: note.trim() || null, reason: reason.trim() || null });
      toast.success(`Gate ${ok ? "confirmed green" : "set red"} — recorded in Gate History.`);
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`gate-confirm-${gate.key}`}>
          {gate.ok ? "Update" : "Confirm"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle>{gate.label}</DialogTitle>
          <DialogDescription>{gate.detail} Every change is written permanently to the Gate History Log with your admin identity and timestamp.</DialogDescription>
        </DialogHeader>
        <Label className="text-xs">Evidence (test runs, rehearsal references, testers)</Label>
        <Input className="mt-1" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)}
               data-testid={`gate-note-${gate.key}`} />
        <Label className="text-xs mt-2">Reason / comment (why is this changing?)</Label>
        <Input className="mt-1" value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)}
               data-testid={`gate-reason-${gate.key}`} />
        <div className="flex gap-2">
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white flex-1" onClick={() => set(true)}
                  data-testid={`gate-green-${gate.key}`}>
            Mark GREEN
          </Button>
          <Button variant="outline" className="text-destructive border-destructive/40 flex-1" onClick={() => set(false)}
                  data-testid={`gate-red-${gate.key}`}>
            Mark RED
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GateRow = ({ gate, onDone }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 py-3 last:border-0"
       data-testid={`gate-row-${gate.key}`}>
    <div className="flex items-start gap-2.5 min-w-0">
      {gate.ok
        ? <CircleCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        : <CircleX className={`h-5 w-5 shrink-0 mt-0.5 ${gate.required_for_live ? "text-red-500" : "text-amber-500"}`} />}
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground break-words">{gate.label}</p>
        <p className="text-[11px] text-muted-foreground break-words">{gate.detail}</p>
        {gate.note && (
          <p className="text-[10px] text-muted-foreground mt-0.5 italic break-words">
            {gate.note} {gate.confirmed_by ? `— ${gate.confirmed_by}, ${(gate.confirmed_at || "").slice(0, 10)}` : ""}
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {gate.required_for_live && (
        <Badge variant="outline" className="bg-secondary text-muted-foreground border-slate-300 text-[9px] uppercase">
          Required
        </Badge>
      )}
      {!gate.auto && <ConfirmGateDialog gate={gate} onDone={onDone} />}
    </div>
  </div>
);

const escHtml = (s) => String(s || "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Printable Gate History export — internal admin record. Append-only source;
 * contains admin identities but no voter data, tokens or private links. */
const buildHistoryHtml = (history) => `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>WitnessYourVote — Gate History Log</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; color: #1a202c; margin: 32px; font-size: 12.5px; }
    h1 { font-size: 19px; margin-bottom: 2px; }
    .meta { color: #4a5568; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; margin-top: 14px; }
    th, td { border: 1px solid #cbd5e0; padding: 5px 7px; text-align: left; vertical-align: top; font-size: 10.5px; }
    th { background: #edf2f7; }
    .g { color: #276749; font-weight: bold; } .r { color: #9b2c2c; font-weight: bold; }
    .foot { margin-top: 20px; font-size: 10px; color: #718096; border-top: 1px solid #cbd5e0; padding-top: 8px; }
    @media print { body { margin: 12mm; } }
  </style></head><body>
  <h1>WitnessYourVote — Gate History Log</h1>
  <p class="meta">Generated: ${escHtml(new Date().toLocaleString())} · ${history.total} change${history.total === 1 ? "" : "s"} recorded ·
  Append-only permanent record — never deleted during normal Admin use. Internal document: contains admin identities;
  no voter personal data, tokens or private links.</p>
  <table>
    <tr><th style="width:22%">Gate</th><th style="width:12%">Status change</th><th style="width:13%">Date / time</th>
    <th style="width:14%">Changed by</th><th style="width:8%">Type</th><th style="width:15%">Reason</th><th>Evidence</th></tr>
    ${history.items.map((h) => `<tr>
      <td>${escHtml(h.gate_label)}</td>
      <td><span class="${h.previous_status === "GREEN" ? "g" : "r"}">${escHtml(h.previous_status || "—")}</span> →
          <span class="${h.new_status === "GREEN" || h.new_status === "UNLOCKABLE" ? "g" : "r"}">${escHtml(h.new_status)}</span></td>
      <td>${escHtml(new Date(h.changed_at).toLocaleString())}</td>
      <td>${escHtml(h.changed_by)}</td>
      <td>${escHtml(h.change_type)}</td>
      <td>${escHtml(h.reason || "—")}</td>
      <td>${escHtml(h.evidence || "—")}</td>
    </tr>`).join("")}
    ${history.items.length === 0 ? `<tr><td colspan="7">No gate changes recorded yet.</td></tr>` : ""}
  </table>
  <p class="foot">Every launch gate change — manual admin confirmations and automatic composite transitions
  (Human Rehearsal, Ward Coverage, Email Mode, overall lock) — is recorded permanently.
  An automated green test is not a substitute for a real person completing the workflow unaided.</p>
  </body></html>`;

const STATUS_BADGE = {
  GREEN: "bg-emerald-50 text-emerald-800 border-emerald-300",
  RED: "bg-red-50 text-red-800 border-red-300",
  LOCKED: "bg-red-50 text-red-800 border-red-300",
  UNLOCKABLE: "bg-emerald-50 text-emerald-800 border-emerald-300",
  PENDING: "bg-amber-50 text-amber-900 border-amber-300",
};
const statusCls = (s) => STATUS_BADGE[s] || "bg-secondary text-foreground border-slate-300";

const HistoryRow = ({ h, idx }) => (
  <div className="border-b border-slate-100 py-3 last:border-0" data-testid={`gate-history-row-${idx}`}>
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <span className="text-sm font-semibold text-foreground break-words">{h.gate_label}</span>
      <Badge variant="outline" className={`text-[10px] ${statusCls(h.previous_status)}`}>
        {h.previous_status || "—"}
      </Badge>
      <span className="text-xs text-muted-foreground">→</span>
      <Badge variant="outline" className={`text-[10px] ${statusCls(h.new_status)}`}>
        {h.new_status}
      </Badge>
      <Badge variant="outline"
             className={`text-[9px] uppercase ${h.change_type === "manual"
               ? "bg-sky-50 text-sky-800 border-sky-300" : "bg-slate-100 text-slate-700 border-slate-300"}`}>
        {h.change_type}
      </Badge>
    </div>
    <p className="text-[11px] text-muted-foreground mt-1 break-words">
      {new Date(h.changed_at).toLocaleString()} — Changed by: {h.changed_by}
    </p>
    {h.reason && (
      <p className="text-[11px] text-foreground/80 break-words">Reason: {h.reason}</p>
    )}
    {h.evidence && (
      <p className="text-[11px] text-muted-foreground italic break-words">Evidence: {h.evidence}</p>
    )}
  </div>
);

export default function LaunchGate() {
  const [data, setData] = useState(null);
  const [bugCount, setBugCount] = useState("");
  const [history, setHistory] = useState({ items: [], total: 0 });

  const load = useCallback(() => {
    adminApi.get("/admin/launch-gate").then((r) => {
      setData(r.data);
      setBugCount(r.data.critical_bugs === null || r.data.critical_bugs === undefined
        ? "" : String(r.data.critical_bugs));
    }).catch(() => setData(null));
    adminApi.get("/admin/launch-gate/history?limit=500")
      .then((r) => setHistory(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const attestBugs = async () => {
    const n = parseInt(bugCount, 10);
    if (Number.isNaN(n) || n < 0) { toast.error("Enter the current open critical bug count."); return; }
    try {
      await adminApi.patch("/admin/launch-gate", { key: "critical_bugs", critical_bugs: n });
      toast.success(`Critical bug count attested: ${n}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  if (!data) return <Skeleton className="h-96" />;

  return (
    <div data-testid="admin-launch-gate-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground inline-flex items-center gap-2">
            <Rocket className="h-6 w-6 text-accent" /> Public Launch Readiness
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">{data.principle}</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="launch-gate-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* THE LOCK — unmistakable */}
      <Card className={`border-2 ${data.live_email_allowed ? "border-emerald-400" : "border-red-300"}`}
            data-testid="launch-gate-lock">
        <CardContent className="p-5 flex flex-wrap items-center gap-3">
          <Lock className={`h-6 w-6 shrink-0 ${data.live_email_allowed ? "text-emerald-600" : "text-red-500"}`} />
          <div className="min-w-0 flex-1 basis-60">
            <p className="font-heading font-bold text-sm text-foreground break-words" data-testid="launch-gate-lock-status">
              {data.live_email_allowed
                ? "ALL REQUIRED GATES GREEN — LIVE email and public launch are unlockable."
                : `LOCKED — ${data.required_red.length} required gate(s) red. LIVE email and any full public-launch switch are impossible until every required gate is green (enforced server-side).`}
            </p>
            {!data.live_email_allowed && (
              <p className="text-[11px] text-muted-foreground mt-0.5 break-words">
                Red: {data.required_red.join(", ")}
              </p>
            )}
          </div>
          <Badge variant="outline" className="bg-slate-100 text-slate-800 border-slate-300 font-bold"
                 data-testid="launch-gate-email-mode">
            EMAIL: {data.email_mode}
          </Badge>
        </CardContent>
      </Card>

      {/* PHASE 12 — PUBLIC BETA (separate soft-launch checklist; never unlocks production) */}
      {data.beta && (
        <Card className="mt-6 border-l-4 border-l-emerald-500" data-testid="launch-gate-group-beta">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
                <Rocket className="h-5 w-5 text-emerald-600" /> PUBLIC BETA
                <Badge variant="outline"
                       className={data.beta.open
                         ? "bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px] font-bold"
                         : "bg-amber-50 text-amber-900 border-amber-300 text-[10px] font-bold"}
                       data-testid="beta-status-badge">
                  BETA STATUS: {data.beta.open ? "OPEN" : "NOT YET OPEN"}
                </Badge>
              </h2>
              <Badge variant="outline" className="bg-secondary text-foreground border-slate-300 text-[10px]">
                {data.beta.items.filter((i) => i.ok).length} / {data.beta.items.length}
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{data.beta.note}</p>
            <div className="mt-3">
              {data.beta.items.map((g) => <GateRow key={g.key} gate={g} onDone={load} />)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FULL PRODUCTION LAUNCH — the strict gates below are unchanged by the Beta */}
      <h2 className="font-heading font-bold text-base text-foreground mt-8 inline-flex items-center gap-2"
          data-testid="production-launch-heading">
        <Lock className="h-4 w-4 text-slate-500" /> FULL PRODUCTION LAUNCH — strict requirements
      </h2>

      {["automated", "human", "system"].map((groupKey) => {
        const meta = GROUP_META[groupKey];
        const Icon = meta.icon;
        const items = data.groups[groupKey] || [];
        const greens = items.filter((i) => i.ok).length;
        return (
          <Card key={groupKey} className="mt-6" data-testid={`launch-gate-group-${groupKey}`}>
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
                  <Icon className="h-5 w-5 text-accent" /> {meta.title}
                  {groupKey === "human" && (
                    <Badge variant="outline"
                           className={data.human_rehearsal_ok
                             ? "bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px]"
                             : "bg-red-50 text-red-800 border-red-300 text-[10px]"}
                           data-testid="human-rehearsal-badge">
                      Human Rehearsal: {data.human_rehearsal_ok ? "PASSED" : "NOT PASSED"}
                    </Badge>
                  )}
                </h2>
                <Badge variant="outline" className="bg-secondary text-foreground border-slate-300 text-[10px]">
                  {greens} / {items.length}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{meta.sub}</p>
              <div className="mt-3">
                {items.map((g) => <GateRow key={g.key} gate={g} onDone={load} />)}
              </div>
              {groupKey === "system" && (
                <div className="flex flex-wrap items-end gap-2 mt-4 rounded-lg border border-slate-200 bg-secondary p-3">
                  <div>
                    <Label className="text-xs inline-flex items-center gap-1">
                      <Bug className="h-3.5 w-3.5" /> Attest current open critical bug count (must be 0)
                    </Label>
                    <Input type="number" min={0} className="mt-1 w-32 bg-white" value={bugCount}
                           onChange={(e) => setBugCount(e.target.value)} data-testid="launch-gate-bugs-input" />
                  </div>
                  <Button size="sm" variant="outline" onClick={attestBugs} data-testid="launch-gate-bugs-attest">
                    Attest
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* GATE HISTORY — append-only, never deleted during normal Admin use */}
      <Card className="mt-6" data-testid="launch-gate-history">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
              <History className="h-5 w-5 text-accent" /> Gate History
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-secondary text-foreground border-slate-300 text-[10px]"
                     data-testid="gate-history-count">
                {history.total} change{history.total === 1 ? "" : "s"} recorded
              </Badge>
              <Button size="sm" variant="outline"
                      onClick={() => {
                        const w = window.open("", "_blank", "width=980,height=760");
                        if (!w) { toast.error("Pop-up blocked — allow pop-ups to print the history."); return; }
                        w.document.write(buildHistoryHtml(history));
                        w.document.close(); w.focus();
                        setTimeout(() => w.print(), 300);
                      }}
                      data-testid="gate-history-print-btn">
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / Save Gate History
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Permanent, append-only record of every gate change — manual admin confirmations and
            automatic composite transitions (Human Rehearsal, Ward Coverage, Email Mode, overall lock).
            Gate history is never deleted during normal Admin use.
          </p>
          <div className="mt-3">
            {history.items.length === 0 ? (
              <p className="text-sm text-muted-foreground italic" data-testid="gate-history-empty">
                No gate changes recorded yet. The first manual confirmation or automatic transition
                will appear here permanently.
              </p>
            ) : (
              history.items.map((h, idx) => <HistoryRow key={h.id} h={h} idx={idx} />)
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
